import { FileUpload } from '@consigli/facade';
import { ExtendedFile } from '@consigli/hooks';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface ReportFileUploadsProps {
  templateFiles: ExtendedFile[];
  setTemplateFiles: React.Dispatch<React.SetStateAction<ExtendedFile[]>>;
  documentFiles: ExtendedFile[];
  setDocumentFiles: React.Dispatch<React.SetStateAction<ExtendedFile[]>>;
}

export const ReportFileUploads: FC<ReportFileUploadsProps> = ({
  templateFiles,
  setTemplateFiles,
  documentFiles,
  setDocumentFiles,
}) => {
  const { t } = useTranslation();

  const handleTemplateFilesChange = useCallback(
    (filesOrUpdater: ExtendedFile[] | ((prev: ExtendedFile[]) => ExtendedFile[])) => {
      const files =
        typeof filesOrUpdater === 'function' ? filesOrUpdater(templateFiles) : filesOrUpdater;

      if (files.length > 1) {
        toast.warning(
          t(
            'package-creation.reports.single-sample-report-only',
            'Only one sample report is allowed. Using the first file.',
          ),
        );
        setTemplateFiles([files[0]]);
      } else {
        setTemplateFiles(files);
      }
    },
    [setTemplateFiles, templateFiles, t],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6 items-start">
      <div className="flex flex-col">
        <div className="mb-2 min-h-[60px]">
          <p className="text-base font-semibold text-day-neutral-subtle">
            {t('package-creation.reports.sample-report-label', 'Sample Report')}{' '}
            <span className="text-day-error-default">*</span>
          </p>
          <p className="text-xs text-day-neutral-subtle mt-1">
            {t(
              'package-creation.reports.sample-report-hint',
              'Upload one sample report file (.docx, .md, etc.) that defines the report structure',
            )}
          </p>
        </div>
        <FileUpload
          files={templateFiles}
          setFiles={handleTemplateFilesChange}
          dropAreaLabel={t('document-upload.drag-n-drop-here', 'Drag and drop here')}
          dropAreaClassName="mt-2 h-48 w-full"
          fileButtonLabel={t('document-upload.browse-files', 'Browse files')}
          folderButtonLabel={t('document-upload.browse-folders', 'Browse folders')}
          uploadListLabel={t('package-creation.reports.sample-report-uploaded', 'Sample report')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
        />
      </div>

      <div className="flex flex-col">
        <div className="mb-2 min-h-[60px]">
          <p className="text-base font-semibold text-day-neutral-subtle">
            {t('package-creation.reports.document-files-label', 'Documents')}{' '}
            <span className="text-day-error-default">*</span>
          </p>
          <p className="text-xs text-day-neutral-subtle mt-1">
            {t(
              'package-creation.reports.document-files-hint',
              'Upload project specific documents to analyze and generate the report',
            )}
          </p>
        </div>
        <FileUpload
          files={documentFiles}
          setFiles={setDocumentFiles}
          dropAreaLabel={t('document-upload.drag-n-drop-here', 'Drag and drop here')}
          dropAreaClassName="mt-2 h-48 w-full"
          fileButtonLabel={t('document-upload.browse-files', 'Browse files')}
          folderButtonLabel={t('document-upload.browse-folders', 'Browse folders')}
          uploadListLabel={t('package-creation.reports.document-files-uploaded', 'Documents')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
        />
      </div>
    </div>
  );
};
